/**
 * ReportsConfigSettings (iter290-b)
 * ---------------------------------
 * Admin panel for the Reports module:
 *   • Enable/disable predefined KPI cards
 *   • Create / edit / delete / reorder / toggle custom Report Cards
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Save, Loader2, Plus, Pencil, Trash2, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import DynamicReportBuilder from '../../components/DynamicReportBuilder';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Operator → user-facing label
const OP_LABELS = {
  equals: 'Equals',
  not_equals: 'Not Equals',
  contains: 'Contains',
  does_not_contain: 'Does Not Contain',
  is_empty: 'Is Empty',
  is_not_empty: 'Is Not Empty',
  greater_than: 'Greater Than',
  less_than: 'Less Than',
  gte: 'Greater Than or Equal',
  lte: 'Less Than or Equal',
};

// A short curated set — everything the CRM already uses on other pages.
const ICON_CHOICES = [
  'BarChart3', 'PieChart', 'TrendingUp', 'Flame', 'Star', 'Award', 'Target',
  'CheckCircle2', 'AlertTriangle', 'Users', 'Building2', 'ClipboardList',
  'Phone', 'Mail', 'Calendar', 'DollarSign',
];

const emptyDraft = () => ({
  name: '',
  data_source: 'accounts',
  conditions: [{ field: '', operator: 'equals', value: '' }],
  icon: 'BarChart3',
  order: 0,
  enabled: true,
});

const CardModal = ({ open, initial, onClose, onSaved, meta }) => {
  const [draft, setDraft] = useState(emptyDraft);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(initial ? { ...initial } : emptyDraft());
    setPreview(null);
  }, [open, initial]);

  const source = meta?.data_sources.find((s) => s.key === draft.data_source);
  const availableFields = source?.fields || [];
  const opFor = (field) => {
    const f = availableFields.find((ff) => ff.key === field);
    if (!f) return meta?.operators.text || [];
    return meta?.operators[f.type === 'number' ? 'number' : 'text'] || [];
  };

  const setCond = (i, patch) => {
    setDraft((d) => ({ ...d, conditions: d.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  };
  const addCond = () => setDraft((d) => ({ ...d, conditions: [...d.conditions, { field: '', operator: 'equals', value: '' }] }));
  const rmCond = (i) => setDraft((d) => ({ ...d, conditions: d.conditions.filter((_, idx) => idx !== i) }));

  const runPreview = async () => {
    setPreviewing(true); setPreview(null);
    try {
      const res = await fetch(`${API_BASE}/api/settings/reports/custom/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(draft),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      setPreview(body.count);
    } catch (e) {
      toast.error(`Preview failed: ${e.message || e}`);
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const url = initial ? `${API_BASE}/api/settings/reports/custom/${initial.id}` : `${API_BASE}/api/settings/reports/custom`;
      const method = initial ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', ...authHeader() }, body: JSON.stringify(draft),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      toast.success(initial ? 'Card updated' : 'Card created');
      onSaved?.(body);
      onClose();
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" data-testid="custom-card-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl mt-8 shadow-xl border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{initial ? 'Edit Report Card' : 'Create Report Card'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Card Name</label>
            <input
              value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Hot Leads" maxLength={60}
              data-testid="custom-card-name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Data Source</label>
              <select
                value={draft.data_source}
                onChange={(e) => setDraft({ ...draft, data_source: e.target.value, conditions: [{ field: '', operator: 'equals', value: '' }] })}
                data-testid="custom-card-source"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                {meta?.data_sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Icon</label>
              <select
                value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
                data-testid="custom-card-icon"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                {ICON_CHOICES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600">Conditions (all must match)</label>
              <button type="button" onClick={addCond} data-testid="custom-card-add-condition"
                      className="text-xs text-teal-600 hover:text-teal-700 font-semibold flex items-center gap-1">
                <Plus size={14}/> Add Condition
              </button>
            </div>
            <div className="space-y-2">
              {draft.conditions.map((c, i) => {
                const opsForField = opFor(c.field);
                const needsValue = c.operator !== 'is_empty' && c.operator !== 'is_not_empty';
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <select
                      value={c.field} onChange={(e) => setCond(i, { field: e.target.value, operator: 'equals' })}
                      data-testid={`custom-card-cond-${i}-field`}
                      className="col-span-4 px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      <option value="">Select field…</option>
                      {availableFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                    <select
                      value={c.operator} onChange={(e) => setCond(i, { operator: e.target.value })}
                      data-testid={`custom-card-cond-${i}-op`}
                      className="col-span-3 px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                    >
                      {opsForField.map((op) => <option key={op} value={op}>{OP_LABELS[op] || op}</option>)}
                    </select>
                    <input
                      value={needsValue ? (c.value ?? '') : ''} onChange={(e) => setCond(i, { value: e.target.value })}
                      disabled={!needsValue} placeholder="Value"
                      data-testid={`custom-card-cond-${i}-value`}
                      className="col-span-4 px-2 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100"
                    />
                    <button type="button" onClick={() => rmCond(i)} disabled={draft.conditions.length === 1}
                            data-testid={`custom-card-cond-${i}-remove`}
                            className="col-span-1 p-2 text-red-500 disabled:opacity-30 hover:bg-red-50 rounded-lg">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Display Order</label>
              <input
                type="number" value={draft.order || 0} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })}
                data-testid="custom-card-order"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <p className="text-[11px] text-gray-400 mt-1">Higher = appears later. Predefined cards use 1–8.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Enabled</label>
              <button type="button" onClick={() => setDraft({ ...draft, enabled: !draft.enabled })}
                      data-testid="custom-card-enabled-toggle"
                      className={`h-10 w-16 rounded-full flex items-center px-1 ${draft.enabled ? 'bg-teal-600' : 'bg-gray-300'}`}>
                <span className={`h-8 w-8 rounded-full bg-white shadow transition-transform ${draft.enabled ? 'translate-x-6' : ''}`} />
              </button>
            </div>
          </div>

          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Current Count: <span className="font-bold text-gray-900 text-lg tabular-nums" data-testid="custom-card-preview-count">{preview === null ? '—' : preview.toLocaleString()}</span>
            </div>
            <button type="button" onClick={runPreview} disabled={previewing}
                    data-testid="custom-card-preview"
                    className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-100 flex items-center gap-1.5">
              {previewing ? <Loader2 size={14} className="animate-spin"/> : <Eye size={14}/>} Preview
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-100" data-testid="custom-card-cancel">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="custom-card-save"
                  className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60 flex items-center gap-1.5">
            {saving && <Loader2 size={14} className="animate-spin"/>}
            {initial ? 'Save Changes' : 'Create Report Card'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportsConfigSettings = () => {
  const [predefined, setPredefined] = useState(null);
  const [custom, setCustom] = useState({ cards: [], data_sources: [], operators: {} });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [pRes, cRes] = await Promise.all([
        fetch(`${API_BASE}/api/settings/reports`, { headers: authHeader() }),
        fetch(`${API_BASE}/api/settings/reports/custom`, { headers: authHeader() }),
      ]);
      if (!pRes.ok) throw new Error(`predefined: HTTP ${pRes.status}`);
      if (!cRes.ok) throw new Error(`custom: HTTP ${cRes.status}`);
      setPredefined(await pRes.json());
      setCustom(await cRes.json());
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePredef = (key) => setPredefined((s) => ({ ...s, enabled: { ...s.enabled, [key]: !s.enabled[key] } }));
  const savePredef = async () => {
    if (!predefined) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/reports`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ enabled: predefined.enabled }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setPredefined(body);
      toast.success('Reports Configuration saved');
    } catch (e) { toast.error(`Save failed: ${e.message || e}`); }
    finally { setSaving(false); }
  };

  const toggleCustom = async (card) => {
    const patched = { ...card, enabled: !card.enabled };
    setCustom((c) => ({ ...c, cards: c.cards.map((k) => (k.id === card.id ? patched : k)) }));
    try {
      await fetch(`${API_BASE}/api/settings/reports/custom/${card.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(patched),
      });
    } catch (e) { toast.error(`Toggle failed: ${e.message || e}`); }
  };

  const removeCustom = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/reports/custom/${id}`, {
        method: 'DELETE', headers: authHeader(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCustom((c) => ({ ...c, cards: c.cards.filter((k) => k.id !== id) }));
      toast.success('Report card deleted');
    } catch (e) { toast.error(`Delete failed: ${e.message || e}`); }
    finally { setConfirmDelete(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-teal-600"/></div>;
  if (err) return <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">Failed to load: {err}</div>;
  if (!predefined) return null;

  return (
    <div className="space-y-8" data-testid="reports-config-settings">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-800">Reports Configuration</h3>
          <p className="text-sm text-gray-500 mt-1">Toggle the built-in KPI cards on/off and create your own Report Cards without touching any code.</p>
        </div>
        <button type="button" onClick={() => { setEditing(null); setModalOpen(true); }}
                data-testid="reports-create-custom-card"
                className="px-4 py-2 bg-teal-600 text-white rounded-lg flex items-center gap-2 hover:bg-teal-700 shrink-0">
          <Plus size={16}/> Create Report Card
        </button>
      </div>

      {/* Predefined */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Predefined Cards</h4>
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          {predefined.order.map((key) => {
            const on = !!predefined.enabled[key];
            return (
              <div key={key} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50">
                <div>
                  <div className="font-medium text-gray-800 text-sm">{predefined.labels[key]}</div>
                  <div className="text-xs text-gray-400 font-mono">{key}</div>
                </div>
                <button type="button" onClick={() => togglePredef(key)}
                        data-testid={`reports-toggle-${key}`}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${on ? 'bg-teal-600' : 'bg-gray-300'}`}
                        aria-pressed={on}>
                  <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`}/>
                </button>
              </div>
            );
          })}
        </div>
        <button type="button" onClick={savePredef} disabled={saving} data-testid="reports-config-save"
                className="mt-3 px-4 py-2 bg-teal-600 text-white rounded-lg flex items-center hover:bg-teal-700 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="mr-2 animate-spin"/> : <Save size={18} className="mr-2"/>}
          Save Predefined Toggles
        </button>
      </div>

      {/* Custom */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Custom Cards ({custom.cards.length})</h4>
        {custom.cards.length === 0 ? (
          <div className="p-6 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-center text-sm text-gray-500" data-testid="reports-no-custom-cards">
            No custom cards yet. Click <b>Create Report Card</b> above to add one.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
            {custom.cards.map((card) => (
              <div key={card.id} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50" data-testid={`custom-card-row-${card.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-800 text-sm">{card.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                    Source: <span className="font-medium">{custom.data_sources.find((s) => s.key === card.data_source)?.label || card.data_source}</span>
                    &nbsp;· Order {card.order || 0} · {card.conditions.length} condition{card.conditions.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" onClick={() => toggleCustom(card)} data-testid={`custom-card-toggle-${card.id}`}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${card.enabled ? 'bg-teal-600' : 'bg-gray-300'}`}>
                    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${card.enabled ? 'translate-x-5' : 'translate-x-0.5'}`}/>
                  </button>
                  <button type="button" onClick={() => { setEditing(card); setModalOpen(true); }}
                          data-testid={`custom-card-edit-${card.id}`}
                          className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg"><Pencil size={14}/></button>
                  <button type="button" onClick={() => setConfirmDelete(card)}
                          data-testid={`custom-card-delete-${card.id}`}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14}/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* iter316 — Dynamic Report Builder */}
      <DynamicReportBuilder />

      <CardModal
        open={modalOpen}
        initial={editing}
        meta={custom}
        onClose={() => setModalOpen(false)}
        onSaved={() => load()}
      />

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <h4 className="text-lg font-bold text-gray-900">Delete this report card?</h4>
            <p className="text-sm text-gray-600 mt-2">This will only remove the report card configuration and will not delete any CRM data.</p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={() => removeCustom(confirmDelete.id)} data-testid="confirm-delete-yes"
                      className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsConfigSettings;
